import React from 'react';
import { AlertTriangle, Building2, CreditCard, LifeBuoy, LogOut } from 'lucide-react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import './ExpiredCenterAccessGate.css';

const ALLOWED_PATHS = new Set([
  '/administration/pulsora-subscription',
  '/support',
  '/centers',
  '/center-information',
]);

const formatDate = value => {
  const raw = String(value || '').slice(0, 10);
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(raw);
  if (!match) return '';
  return `${match[3]}/${match[2]}/${match[1]}`;
};

export default function ExpiredCenterAccessGate({ children }) {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  const center = user?.restricted_center || {};
  const expiration = formatDate(
    center.fecha_vencimiento_membresia
  );

  const allowed = ALLOWED_PATHS.has(location.pathname);

  const title = center.nombre
    ? `La membresía de ${center.nombre} está vencida`
    : 'Tu membresía Pulsora está vencida';

  const detail = expiration
    ? `Venció el ${expiration}. El Centro permanece temporalmente inhabilitado hasta completar su reactivación.`
    : 'El Centro permanece temporalmente inhabilitado hasta completar su reactivación.';

  if (allowed) {
    return (
      <>
        <section
          className="expired-center-status-banner"
          role="status"
          aria-live="polite"
        >
          <div className="expired-center-status-copy">
            <AlertTriangle size={21} aria-hidden="true" />
            <div>
              <strong>Membresía Pulsora vencida</strong>
              <span>{detail}</span>
            </div>
          </div>

          <div className="expired-center-status-actions">
            <button
              type="button"
              onClick={() =>
                navigate('/administration/pulsora-subscription')
              }
            >
              <CreditCard size={17} />
              Reactivar membresía
            </button>

            <button
              type="button"
              className="secondary"
              onClick={() => navigate('/support')}
            >
              <LifeBuoy size={17} />
              Soporte
            </button>
          </div>
        </section>

        {children}
      </>
    );
  }

  return (
    <main className="expired-center-gate">
      <section className="expired-center-card">
        <div className="expired-center-icon">
          <AlertTriangle size={34} aria-hidden="true" />
        </div>

        <span className="expired-center-eyebrow">
          MEMBRESÍA VENCIDA
        </span>

        <h1>{title}</h1>

        <p>{detail}</p>

        <p className="expired-center-explanation">
          Tu cuenta Pulsora continúa activa. Mientras regularizas la
          membresía puedes gestionar el perfil básico del Centro,
          consultar Facturación Pulsora y comunicarte con Soporte.
        </p>

        <div className="expired-center-primary-actions">
          <button
            type="button"
            className="primary"
            onClick={() =>
              navigate('/administration/pulsora-subscription')
            }
          >
            <CreditCard size={18} />
            Reactivar membresía
          </button>

          <button
            type="button"
            onClick={() => navigate('/centers')}
          >
            <Building2 size={18} />
            Editar perfil
          </button>

          <button
            type="button"
            onClick={() => navigate('/support')}
          >
            <LifeBuoy size={18} />
            Soporte
          </button>
        </div>

        <div className="expired-center-access-note">
          <strong>Acceso temporal disponible</strong>
          <span>
            {'Facturaci\u00f3n Pulsora \u00b7 Perfil b\u00e1sico \u00b7 Soporte'}
          </span>
        </div>

        <button
          type="button"
          className="expired-center-logout"
          onClick={() => logout()}
        >
          <LogOut size={16} />
          Cerrar sesión
        </button>
      </section>
    </main>
  );
}
